/**
 * clientErrorReport.js — F-118 client-side error telemetry (structural blind-spot fix).
 *
 * F-114 and F-115 were both BLINDED: no client-side error telemetry exists anywhere, so when a real
 * customer's device (Helen, Kindle Fire / Silk) lost typed work, we could not see what her browser did.
 * This ends that era. `window.onerror` + `unhandledrejection` -> a SANITIZED report POSTed to the
 * EXISTING `/feedback` path (reuse; no new vendor, no new endpoint). Message + stack + bundle + route
 * ONLY — NEVER customer content (no story text, no answers, no query string). Rate-limited + deduped so
 * an error loop can never spam the backend or the customer's network.
 *
 * Wiring (its own commit, after F-116): in App.js, once Amplify is configured and the user is signed in
 * (the `/feedback` path requires auth), call
 *   installClientErrorReporting({ send: (p) => callAi(p, { path: '/feedback' }).catch(() => {}), appVersion: APP_VERSION });
 */

/** The deployed bundle hash (main.<hash>.js) from the loaded scripts, else the app version. */
export function getBundleTag(doc, appVersion) {
  try {
    const s = (doc || (typeof document !== 'undefined' ? document : null));
    const el = s && s.querySelector && s.querySelector('script[src*="/static/js/main."]');
    const m = el && el.src && el.src.match(/main\.([a-z0-9]+)\.js/i);
    if (m) return m[1];
  } catch (e) { /* ignore */ }
  return appVersion || 'unknown';
}

/** pathname + hash only — the query string is dropped (it can carry tokens / PII). */
export function sanitizeRoute(loc) {
  const l = loc || (typeof window !== 'undefined' ? window.location : { pathname: '', hash: '' });
  return `${l.pathname || ''}${l.hash || ''}`.slice(0, 120);
}

/** Build the sanitized report. Only error internals + environment — never customer content. */
export function buildErrorReport({ kind, message, stack, bundle, route, ua }) {
  const clip = (s, n) => (typeof s === 'string' ? s.replace(/\s+/g, ' ').trim().slice(0, n) : '');
  return {
    action: 'submit',
    type: 'client_error',
    kind: kind || 'error', // 'error' | 'unhandledrejection'
    message: clip(message, 300),
    stack: clip(stack, 1500),
    bundle: bundle || 'unknown',
    route: route || '',
    ua: clip(ua, 200),
  };
}

/**
 * Install the global error handlers. `send(payload)` delivers the report (e.g. via callAi -> /feedback).
 * Idempotent, rate-limited (maxReports/session), deduped (by kind+message), and never throws. Returns an
 * uninstall function.
 */
export function installClientErrorReporting({ win, send, appVersion, maxReports = 8 } = {}) {
  const w = win || (typeof window !== 'undefined' ? window : null);
  if (!w || typeof w.addEventListener !== 'function') return () => {};
  if (w.__tbErrReportInstalled) return () => {};
  w.__tbErrReportInstalled = true;
  const seen = new Set();
  let count = 0;
  const report = (kind, message, stack) => {
    try {
      if (count >= maxReports) return;
      const key = (kind + '|' + (message || '')).slice(0, 200);
      if (seen.has(key)) return;
      seen.add(key);
      count++;
      send(buildErrorReport({
        kind,
        message,
        stack,
        bundle: getBundleTag(w.document, appVersion),
        route: sanitizeRoute(w.location),
        ua: w.navigator && w.navigator.userAgent,
      }));
    } catch (e) { /* telemetry must never throw into the app */ }
  };
  const onError = (ev) => report('error', ev && (ev.message || (ev.error && ev.error.message)), ev && ev.error && ev.error.stack);
  const onRej = (ev) => {
    const r = ev && ev.reason;
    report('unhandledrejection', r && (r.message || String(r)), r && r.stack);
  };
  w.addEventListener('error', onError);
  w.addEventListener('unhandledrejection', onRej);
  return () => {
    try {
      w.removeEventListener('error', onError);
      w.removeEventListener('unhandledrejection', onRej);
      w.__tbErrReportInstalled = false;
    } catch (e) { /* ignore */ }
  };
}
